'use client'

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { X, ChevronDown, ChevronUp } from 'lucide-react'
import { createPortal } from 'react-dom'

import type { Header as HeaderType } from '@/payload-types'

import { CMSLink } from '@/components/Link'
import { useLenis } from '@/providers/LenisProvider'

interface MobileMenuProps {
  isOpen: boolean
  onClose: () => void
  data: HeaderType
}

export const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose, data }) => {
  const navItems = data?.navItems || []
  const [expandedItems, setExpandedItems] = useState<number | null>(null)
  const [viewportHeight, setViewportHeight] = useState<number>(0)
  const { lenis } = useLenis()

  // Update viewport height when menu opens and on resize
  useEffect(() => {
    if (isOpen) {
      const updateHeight = () => {
        setViewportHeight(window.innerHeight)
      }

      // Set initial height
      updateHeight()

      // Listen for viewport changes (iOS Safari URL bar)
      window.addEventListener('resize', updateHeight)
      window.addEventListener('orientationchange', updateHeight)

      return () => {
        window.removeEventListener('resize', updateHeight)
        window.removeEventListener('orientationchange', updateHeight)
      }
    }
  }, [isOpen])

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      // Store the current scroll position
      const scrollY = window.scrollY

      // Apply styles to prevent scrolling
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
      document.body.style.overflow = 'hidden'

      return () => {
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        document.body.style.overflow = ''
        window.scrollTo(0, scrollY)
        lenis?.scrollTo(scrollY, { immediate: true })
      }
    }
  }, [isOpen])

  const toggleExpanded = (index: number) => {
    setExpandedItems(index)
  }

  const handleMainLinkClick = (index: number, hasSubLinks: boolean) => {
    if (hasSubLinks) {
      toggleExpanded(index)
    } else {
      onClose()
    }
  }

  // Helper function to extract URL from a link object
  const getLinkUrl = (link: any): string | null => {
    if (link.url) {
      return link.url
    }
    if (
      link.reference?.value &&
      typeof link.reference.value === 'object' &&
      link.reference.value.slug
    ) {
      const relationTo = link.reference.relationTo
      return relationTo !== 'pages'
        ? `/${relationTo}/${link.reference.value.slug}`
        : `/${link.reference.value.slug}`
    }
    return null
  }

  // Helper function to filter out duplicate sublinks
  const getFilteredSubLinks = (mainLink: any, subLinks: any[]) => {
    const mainUrl = getLinkUrl(mainLink)
    if (!mainUrl) return subLinks

    const filtered = subLinks.filter((subLink) => {
      const subUrl = getLinkUrl(subLink.link)
      return subUrl !== mainUrl
    })

    return filtered
  }

  if (typeof window === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
          onTouchMove={(e) => e.preventDefault()}
        >
          <div
            className="flex flex-col bg-custom-card-background"
            style={{
              height: viewportHeight > 0 ? `${viewportHeight}px` : '100dvh',
              maxHeight: viewportHeight > 0 ? `${viewportHeight}px` : '100dvh',
            }}
            onClick={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex flex-shrink-0 items-center justify-end p-6">
              <button
                onClick={onClose}
                className="flex h-10 w-10 items-center justify-center rounded-lg text-white transition-colors hover:bg-white/20"
                aria-label="Close menu"
              >
                <X className="h-8 w-8" />
              </button>
            </div>

            {/* Navigation Items */}
            <nav className="flex-1 overflow-y-auto px-6 pb-12">
              <div className="space-y-6">
                {navItems.map(({ link, group, disableMainLink }, index) => {
                  const shouldDisableMainLink =
                    disableMainLink && group?.links && group.links.length > 0
                  const filteredSubLinks = group?.links
                    ? shouldDisableMainLink
                      ? group.links // When main link is disabled, show all sublinks without filtering
                      : getFilteredSubLinks(link, group.links) // When main link is enabled, filter duplicates
                    : []
                  const hasSubLinks = Boolean(filteredSubLinks.length > 0)
                  const isExpanded = expandedItems === index

                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="relative w-max">
                            {hasSubLinks ? (
                              <button
                                onClick={() => handleMainLinkClick(index, hasSubLinks)}
                                className="flex-1 pb-2 pr-8 text-left text-xl font-semibold text-white transition-colors hover:text-accent"
                              >
                                {link.label}
                              </button>
                            ) : (
                              <CMSLink
                                {...link}
                                appearance="link"
                                className="block pb-2 text-xl font-semibold text-white transition-colors hover:text-accent px-0"
                                onClick={onClose}
                              ></CMSLink>
                            )}
                            {/* Animated Underline */}
                            <motion.div
                              className="absolute bottom-0 left-0 h-1 bg-accent"
                              initial={{ width: 0 }}
                              animate={{
                                width: isExpanded ? '100%' : 0,
                                opacity: isExpanded ? 1 : 0,
                              }}
                              transition={{
                                duration: 0.3,
                                ease: [0.25, 0.46, 0.45, 0.94],
                              }}
                            />
                          </div>
                          {hasSubLinks && (
                            <button
                              onClick={() => toggleExpanded(index)}
                              className="ml-2 flex h-8 w-8 items-center justify-center text-white transition-colors hover:text-accent"
                            ></button>
                          )}
                        </div>

                        {hasSubLinks && (
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                                className="ml-4 space-y-2 overflow-hidden"
                              >
                                {/* Main link - only show if not disabled */}
                                {!shouldDisableMainLink && (
                                  <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0 }}
                                  >
                                    <CMSLink
                                      {...link}
                                      appearance="link"
                                      className="block text-gray-300 transition-colors hover:text-accent"
                                      onClick={onClose}
                                    />
                                  </motion.div>
                                )}

                                {/* Sub links */}
                                {filteredSubLinks.map((subLink, subIndex) => (
                                  <motion.div
                                    key={subIndex}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{
                                      delay: (subIndex + (shouldDisableMainLink ? 0 : 1)) * 0.05,
                                    }}
                                  >
                                    <CMSLink
                                      {...subLink.link}
                                      appearance="link"
                                      className="block text-gray-300 transition-colors hover:text-accent"
                                      onClick={onClose}
                                    />
                                  </motion.div>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        )}
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </nav>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
